import React, { useEffect } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { bindAction } from "interfaces";
import { intersection } from "lodash";

import { RolePermissions, ViewType } from "../../../../../enums";
import { RemovePackFromAccountConfirmationModal, RestrictedByTooltip } from "../../../../../components";
import { type AccountPack, AccountTypes } from "../../../types";
import {
  AddAllPacksToOwnAccountSuccess,
  AddPacksLicensesToAccountSuccess,
  ConvertTrialLicenseAllSuccess,
  PackArchiveSuccess,
  RemovePackFromAccountSuccess,
  UpdatePackAccountLicenseSuccess,
} from "features/Application/services/realTimeNotification/events/accounts/accountsEvents";
import * as notificationsActions from "../../../../Notifications/state/notificationsActions";
import * as backgroundTasksActions from "../../../../BackgroundTasks/state/backgroundTasksActions";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { AccountPackMenu } from "features/shared/Packs/AccountPackMenu/AccountPackMenu";
import RtnEventsEmitter from "../../../../Application/services/realTimeNotification/rtnEventsEmitter";
import { BasePacksTable, type BasePacksTableProps } from "../BasePacksTable/BasePacksTable";
import { getPurchasePacksAddonEnabled } from "features/Accounts/state/selectors/addonsSelector";
import { archivePack, removePackFromAccount } from "features/Accounts/state/thunks/accountPacksThunk";
import { fetchAddOns } from "features/Accounts/state/thunks/addOnsThunk";
import ArchivePackConfirmationDialog from "./archivePackConfirmationDialog/ArchivePackConfirmationDialog";

export interface AccountPacksListProps extends BasePacksTableProps {
  handleEditLicensingClick: (id: number, packPricingTypeId?: number) => void;
  handleExtendLicensesClick: (id: number, title: string, thumbnailImageUrl: string, packPricingTypeId?: number) => void;
  handleRestorePackClick: (id: number, title: string, thumbnailImageUrl: string, packPricingTypeId?: number) => void;
  handleConvertTrialLicensingClick: (id: number, packPricingTypeId?: number) => void;
  triggerRefreshPacksGrid: (viewType: ViewType) => void;
  selectedAccountId: number;
  permissionsToAccount: RolePermissions[];
  enabled: boolean;
}

export type AccountPacksListAllProps = AccountPacksListProps & PropsFromRedux;

export function AccountPacksList(props: AccountPacksListAllProps) {
  const packsRefreshRtnEvents = [
    AddPacksLicensesToAccountSuccess,
    UpdatePackAccountLicenseSuccess,
    RemovePackFromAccountSuccess,
    PackArchiveSuccess,
    AddAllPacksToOwnAccountSuccess,
    ConvertTrialLicenseAllSuccess,
  ];
  const {
    triggerRefreshPacksGrid,
    accountId,
    isAccountHome,
    selectedAccountId,
    hasPurchasePower,
    enabled,
    accountPacksActions,
    accountTypeId,
    hasPurchasePackAddon,
    addons,
  } = props;

  const [triggerOpenRemovePackDialog, setTriggerOpenRemovePackDialog] = React.useState(false);
  const [triggerArchivePackDialog, setTriggerArchivePackDialog] = React.useState(false);
  const [selectedPackId, setSelectedPackId] = React.useState(-1);
  const hasAccountManageRole =
    intersection(props.permissionsToAccount, [RolePermissions.AccountsSettingsManage]).length > 0;

  useEffect(() => {
    if (!addons?.items.length && !addons.isLoading) {
      props.fetchAddOns(accountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    RtnEventsEmitter.subscribe(packsRefreshRtnEvents, refreshPacksGrid);

    return () => {
      RtnEventsEmitter.unsubscribe(packsRefreshRtnEvents, refreshPacksGrid);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshPacksGrid = () => {
    triggerRefreshPacksGrid(ViewType.GRID);
  };

  const renderActionCell = (pack: AccountPack) => {
    const isOwnPack = pack.isOwn;

    if (!enabled || !addons?.items.length) {
      return null;
    }

    return (
      <RestrictedByTooltip hasPermission={hasAccountManageRole}>
        <AccountPackMenu
          id={pack.id}
          title={pack.title}
          thumbnailImageUrl={pack.thumbnailImageUrl}
          isExpired={pack.isExpired}
          isTrial={pack.isTrial}
          isArchived={pack.isArchived || false}
          onEditLicensing={props.handleEditLicensingClick}
          onExtendLicenses={props.handleExtendLicensesClick}
          onConvertTrialLicense={props.handleConvertTrialLicensingClick}
          onRemovePackFromAccount={handleRemovePackFromAccountClick}
          isAllowedToRemovePackFromAccount={isAllowedToRemovePackFromAccount}
          hasAccountManageRole={hasAccountManageRole}
          hasPurchasePower={hasPurchasePower}
          isAccountHome={isAccountHome}
          onArchivePack={pack.isArchived ? undefined : handleArchivePack}
          onRestorePack={pack.isArchived ? props.handleRestorePackClick : undefined}
          isOwnPack={isOwnPack}
          hasSource={pack.hasSource}
          packPricingTypeId={pack.packPricingTypeId}
          licenseCount={pack.licensesCount || 0}
          expirationDate={pack.expirationDate}
          hasGlobalAccess={accountTypeId === AccountTypes.BSI || accountTypeId === AccountTypes.ChannelPartner}
          hasPurchasePackAddon={hasPurchasePackAddon}
          pricePerUserPerMonth={pack.pricePerUserPerMonth}
        />
      </RestrictedByTooltip>
    );
  };

  const onContinuePackRemove = () => {
    setTriggerOpenRemovePackDialog(false);
    accountPacksActions.removePackFromAccount(selectedAccountId, selectedPackId);
  };

  const handleRemovePackFromAccountClick = async (packId: number) => {
    setSelectedPackId(packId);
    setTriggerOpenRemovePackDialog(true);
  };

  const onCancelPackRemove = () => {
    setSelectedPackId(-1);
    setTriggerOpenRemovePackDialog(false);
  };

  const isAllowedToRemovePackFromAccount = () => {
    return hasAccountManageRole;
  };

  const onCancelArchivePack = () => {
    setSelectedPackId(-1);
    setTriggerArchivePackDialog(false);
  };

  const onContinueArchivePack = async () => {
    setTriggerArchivePackDialog(false);
    accountPacksActions.archivePack(isAccountHome ? accountId : selectedAccountId, selectedPackId);
  };

  const handleArchivePack = (packId: number) => {
    setTriggerArchivePackDialog(true);
    setSelectedPackId(packId);
  };

  const cardButtonHandlers = {
    onEditLicensing: props.handleEditLicensingClick,
    onExtendLicenses: props.handleExtendLicensesClick,
    onRemovePackFromAccount: handleRemovePackFromAccountClick,
    isAllowedToRemovePackFromAccount,
    onArchivePack: handleArchivePack,
    onRestorePack: props.handleRestorePackClick,
    onConvertTrialLicense: props.handleConvertTrialLicensingClick,
  };

  return (
    <>
      <BasePacksTable
        {...props}
        cardActionHandlers={cardButtonHandlers}
        renderActionCell={renderActionCell}
        listViewRtnEvents={packsRefreshRtnEvents}
      />

      <RemovePackFromAccountConfirmationModal
        triggerOpen={triggerOpenRemovePackDialog}
        onCancel={onCancelPackRemove}
        onContinue={onContinuePackRemove}
      />

      <ArchivePackConfirmationDialog
        triggerOpen={triggerArchivePackDialog}
        onCancel={onCancelArchivePack}
        onContinue={onContinueArchivePack}
      />
    </>
  );
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  accountTypeId: state.userProfile.accountTypeId,
  moboAccountId: state.accounts.edit.accountInfo.accountConfiguration.id,
  hasPurchasePackAddon: getPurchasePacksAddonEnabled(state),
  addons: state.accounts.addOns,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    notificationsActions: bindActionCreators(notificationsActions, dispatch),
    backgroundTasksActions: bindActionCreators(backgroundTasksActions, dispatch),
    accountPacksActions: {
      removePackFromAccount: bindAction(removePackFromAccount, dispatch),
      archivePack: bindAction(archivePack, dispatch),
    },
    fetchAddOns: bindAction(fetchAddOns, dispatch),
  };
};

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AccountPacksList);
export default ConnectedComponent;
